/* ============================================================
   FORMVOLT theme — global.js
   Topbar-rotator, header, reveals, AJAX-cart, variant-picker,
   collectiefilters. Geen dependencies.
   ============================================================ */

const money = (cents) =>
  new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" }).format(cents / 100);

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

/* ---------- topbar rotator ---------- */
function initTopbar() {
  const bar = document.querySelector("[data-rotator]");
  if (!bar) return;
  const items = [...bar.querySelectorAll("span")];
  if (items.length < 2) return;
  let i = 0;
  setInterval(() => {
    items[i].classList.add("is-out");
    setTimeout(() => {
      items[i].hidden = true;
      items[i].classList.remove("is-out");
      i = (i + 1) % items.length;
      items[i].hidden = false;
    }, 400);
  }, 3800);
}

/* ---------- header & reveals ---------- */
function initHeader() {
  const header = document.querySelector("[data-header]");
  if (!header) return;
  const onScroll = () => header.classList.toggle("is-stuck", window.scrollY > 8);
  window.addEventListener("scroll", onScroll, { passive: true });
  header.addEventListener("keydown", (event) => {
    const menu = header.querySelector(".mobile-menu[open]");
    if (event.key === "Escape" && menu) {
      menu.open = false;
      menu.querySelector("summary").focus();
    }
  });
  onScroll();
}

function initReveals() {
  const els = document.querySelectorAll(".reveal");
  if (!("IntersectionObserver" in window)) {
    els.forEach((el) => el.classList.add("is-in"));
    return;
  }
  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (e.isIntersecting) { e.target.classList.add("is-in"); io.unobserve(e.target); }
    }),
    { threshold: 0.12 }
  );
  els.forEach((el, i) => {
    el.style.transitionDelay = `${Math.min(i % 4, 3) * 60}ms`;
    io.observe(el);
  });
}

/* ---------- cart (Shopify Cart API) ---------- */
const cartUI = {
  busy: false,
  showError(message = "") {
    document.querySelectorAll("[data-cart-error]").forEach((el) => {
      el.textContent = message;
      el.hidden = !message;
    });
  },
  async request(url, options) {
    const res = await fetch(url, options);
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || "De winkelwagen kon niet worden bijgewerkt.");
    return data;
  },
  async perform(action, button) {
    if (this.busy) return;
    this.busy = true;
    this.showError();
    const disabled = button?.disabled;
    if (button) { button.disabled = true; button.setAttribute("aria-busy", "true"); }
    try {
      await action();
    } catch (err) {
      this.showError(err instanceof TypeError
        ? "Geen verbinding. Controleer je winkelwagen voordat je opnieuw toevoegt."
        : err.message || "Bijwerken is niet gelukt. Controleer je winkelwagen en probeer opnieuw.");
      if (!button?.matches("[data-add-button]")) this.open();
    } finally {
      this.busy = false;
      if (button) {
        button.disabled = disabled;
        button.removeAttribute("aria-busy");
        if (button.matches("[data-add-button]")) button.disabled = !document.querySelector("[data-variant-id]")?.value || button.dataset.available === "false";
      }
    }
  },
  open() { document.body.classList.add("cart-open"); },
  close() { document.body.classList.remove("cart-open"); },

  async fetchCart() {
    return this.request(window.FV.routes.cart + ".js");
  },

  async add(variantId, quantity = 1) {
    await this.request(window.FV.routes.cartAdd + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: variantId, quantity }),
    });
    await this.render();
    this.open();
  },

  async changeLine(line, quantity) {
    await this.request(window.FV.routes.cartChange + ".js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ line, quantity }),
    });
    await this.render();
  },

  async render() {
    const cart = await this.fetchCart();
    document.querySelectorAll(".cart-count").forEach((el) => (el.textContent = cart.item_count));

    const list = document.querySelector("[data-cart-items]");
    if (!list) return;

    if (!cart.items.length) {
      list.innerHTML = `<div class="cart-empty"><p>Je winkelwagen is leeg.</p></div>`;
    } else {
      list.innerHTML = cart.items.map((item, idx) => `
        <div class="cart-item">
          <div class="field">
            ${item.image ? `<img src="${esc(item.image.replace(/(\.[a-z]+)(\?|$)/, "_300x$1$2"))}" alt="" loading="lazy">` : ""}
          </div>
          <div>
            <h4>${esc(item.product_title)}</h4>
            ${item.variant_title && item.variant_title !== "Default Title" ? `<p class="mono">${esc(item.variant_title)}</p>` : ""}
            <div class="qty">
              <button data-line="${idx + 1}" data-qty="${item.quantity - 1}" aria-label="Minder">−</button>
              <output>${item.quantity}</output>
              <button data-line="${idx + 1}" data-qty="${item.quantity + 1}" aria-label="Meer">+</button>
            </div>
          </div>
          <span class="cart-item-price">${money(item.final_line_price)}</span>
        </div>`).join("");
    }

    const subEl = document.querySelector("[data-cart-subtotal]");
    if (subEl) subEl.textContent = money(cart.total_price);

    const threshold = window.FV.freeShippingThreshold;
    const bar = document.querySelector("[data-progress-bar]");
    const text = document.querySelector("[data-progress-text]");
    if (bar) bar.style.width = `${(threshold > 0 ? Math.min(100, (cart.total_price / threshold) * 100) : 100)}%`;
    if (text) {
      text.innerHTML = cart.total_price >= threshold
        ? "Je bestelling wordt <strong>gratis verzonden</strong> naar Nederland en België."
        : `Nog <strong>${money(threshold - cart.total_price)}</strong> tot gratis verzending.`;
    }
  },
};

function initCart() {
  cartUI.render().catch(() => cartUI.showError("De winkelwagen kan niet worden geladen. Ververs de pagina en probeer opnieuw."));

  document.querySelectorAll("[data-cart-open]").forEach((b) => b.addEventListener("click", () => cartUI.open()));
  document.querySelector("[data-cart-close]")?.addEventListener("click", () => cartUI.close());
  document.querySelector("[data-cart-overlay]")?.addEventListener("click", () => cartUI.close());
  document.addEventListener("keydown", (e) => { if (e.key === "Escape") cartUI.close(); });

  document.querySelector("[data-cart-items]")?.addEventListener("click", (e) => {
    const btn = e.target.closest("button[data-line]");
    if (btn) cartUI.perform(() => cartUI.changeLine(Number(btn.dataset.line), Number(btn.dataset.qty)), btn);
  });

  /* quick add op productkaarten */
  document.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-quick-add]");
    if (!btn) return;
    e.preventDefault();
    cartUI.perform(() => cartUI.add(Number(btn.dataset.quickAdd)), btn);
  });

  /* PDP: formulier onderscheppen → drawer i.p.v. cart-pagina */
  const form = document.querySelector("[data-add-form]");
  form?.addEventListener("submit", (e) => {
    e.preventDefault();
    const id = Number(form.querySelector("[data-variant-id]").value);
    cartUI.perform(() => cartUI.add(id), form.querySelector("[data-add-button]"));
  });
}

/* ---------- variant picker ---------- */
function initVariantPicker() {
  const pdp = document.querySelector("[data-product]");
  const picker = document.querySelector("[data-variant-picker]");
  const jsonEl = document.querySelector("[data-product-json]");
  if (!pdp || !picker || !jsonEl) return;

  const variants = JSON.parse(jsonEl.textContent);
  const selected = {};
  const lowStockAt = Number(window.FV?.lowStockThreshold) || 5;

  picker.querySelectorAll(".swatch.is-active, .pill.is-active").forEach((el) => {
    selected[el.dataset.optionIndex] = el.dataset.value;
  });

  function currentVariant() {
    return variants.find((v) =>
      Object.entries(selected).every(([idx, val]) => v[`option${idx}`] === val)
    );
  }

  /* Is deze optiewaarde te bestellen met de overige geselecteerde opties? */
  function optionValueAvailable(optionIndex, value) {
    const trial = { ...selected, [String(optionIndex)]: value };
    return variants.some((v) =>
      v.available && Object.entries(trial).every(([idx, val]) => v[`option${idx}`] === val)
    );
  }

  function markSoldOutOptions() {
    picker.querySelectorAll("[data-option-index]").forEach((btn) => {
      const ok = optionValueAvailable(btn.dataset.optionIndex, btn.dataset.value);
      btn.classList.toggle("is-soldout", !ok);
      btn.setAttribute("aria-disabled", ok ? "false" : "true");
      if (btn.classList.contains("swatch")) {
        const base = btn.dataset.value || "";
        btn.setAttribute("aria-label", ok ? base : `${base} — uitverkocht`);
      }
    });
  }

  function updateStockNote(variant) {
    const stockNote = document.querySelector("[data-stock-note]");
    if (!stockNote) return;
    const qty = variant?.inventory_quantity;
    const tracked = variant?.inventory_management;
    if (
      variant?.available &&
      tracked &&
      typeof qty === "number" &&
      qty > 0 &&
      qty <= lowStockAt
    ) {
      stockNote.textContent = `Nog ${qty} op voorraad`;
      stockNote.hidden = false;
    } else {
      stockNote.textContent = "";
      stockNote.hidden = true;
    }
  }

  function update() {
    const variant = currentVariant();
    const priceEl = document.querySelector("[data-price]");
    const idInput = document.querySelector("[data-variant-id]");
    const addBtn = document.querySelector("[data-add-button]");
    const colorLabel = document.querySelector("[data-color-label]");

    if (colorLabel && selected["1"]) colorLabel.textContent = selected["1"];

    markSoldOutOptions();

    picker.querySelectorAll("[data-option-index]").forEach((btn) => {
      const on = selected[btn.dataset.optionIndex] === btn.dataset.value;
      btn.classList.toggle("is-active", on);
      btn.setAttribute("aria-checked", on ? "true" : "false");
    });

    if (!variant) {
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.dataset.available = "false";
        addBtn.textContent = "Niet beschikbaar in deze combinatie";
      }
      updateStockNote(null);
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (priceEl) {
      priceEl.innerHTML = variant.compare_at_price > variant.price
        ? `${money(variant.price)} <s>${money(variant.compare_at_price)}</s>`
        : money(variant.price);
    }
    if (addBtn) {
      addBtn.dataset.available = String(variant.available);
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available
        ? addBtn.dataset.label || "Voeg toe aan winkelwagen"
        : "Tijdelijk uitverkocht";
    }
    updateStockNote(variant);

    filterThumbsByColor();

    document.dispatchEvent(
      new CustomEvent("formvolt:color", { detail: { color: selected[colorOptionIndex()] } })
    );

    if (variant.featured_image?.src) {
      const main = document.querySelector("[data-main-media] img");
      if (main) {
        main.src = variant.featured_image.src;
        main.removeAttribute("srcset");
      }
    }

    const url = new URL(window.location);
    url.searchParams.set("variant", variant.id);
    window.history.replaceState({}, "", url);
  }

  const addBtn = document.querySelector("[data-add-button]");
  if (addBtn) {
    addBtn.dataset.label ||= "Voeg toe aan winkelwagen";
    addBtn.dataset.available = String(!addBtn.disabled);
  }

  picker.addEventListener("click", (e) => {
    const btn = e.target.closest("[data-option-index]");
    if (!btn) return;
    selected[btn.dataset.optionIndex] = btn.dataset.value;
    update();
  });

  function filterThumbsByColor() {
    const wrap = document.querySelector("[data-thumbs]");
    if (!wrap) return;
    const active = selected[colorOptionIndex()];
    if (!active) return;
    const thumbs = [...wrap.querySelectorAll("[data-thumb]")];
    thumbs.forEach((t) => {
      const c = t.dataset.color;
      t.classList.toggle("is-hidden", c !== "" && c !== active);
    });
    const firstVisible = thumbs.find((t) => !t.classList.contains("is-hidden"));
    thumbs.forEach((x) => x.classList.remove("is-active"));
    if (firstVisible) firstVisible.classList.add("is-active");
  }

  function colorOptionIndex() {
    const swatch = picker.querySelector(".swatches [data-option-index]");
    return swatch ? swatch.dataset.optionIndex : null;
  }

  document.querySelectorAll("[data-thumb]").forEach((t) => t.addEventListener("click", () => {
    document.querySelectorAll("[data-thumb]").forEach((x) => x.classList.remove("is-active"));
    t.classList.add("is-active");
    const main = document.querySelector("[data-main-media] img");
    if (main) { main.src = t.dataset.src; main.removeAttribute("srcset"); }
  }));

  markSoldOutOptions();
}

/* ---------- draai-viewer (360) ----------
   Eén turntable-video per kleur. We spelen hem niet af tijdens het slepen maar
   zetten currentTime op basis van de sleepafstand: de volle breedte van het beeld
   is één hele slag. Seeken kost op deze bestanden ~18 ms, dus dat volgt de muis.
   Zonder bron voor de gekozen kleur verdwijnen viewer én knop. */
function initSpin() {
  const spin = document.querySelector("[data-spin]");
  const mapEl = document.querySelector("[data-spin-map]");
  const thumb = document.querySelector("[data-spin-thumb]");
  const mainMedia = document.querySelector("[data-main-media]");
  if (!spin || !mapEl || !thumb || !mainMedia) return;

  let sources;
  try { sources = JSON.parse(mapEl.textContent); } catch (e) { return; }

  const stage = spin.querySelector("[data-spin-stage]");
  const video = spin.querySelector("[data-spin-video]");
  const calm = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let loadedFor = null;   // welke kleur er nu in de video zit
  let wantTime = null;    // gevraagde positie terwijl er nog geseekt wordt
  let seeking = false;
  let introDone = false;
  let dragging = false;

  function wrap(t) {
    const d = video.duration;
    if (!d) return 0;
    return ((t % d) + d) % d;   // doordraaien in dezelfde richting blijft doordraaien
  }

  function pump() {
    if (wantTime === null) { seeking = false; return; }
    const t = wrap(wantTime);
    wantTime = null;
    /* Vraag je exact de stand die er al staat, dan doet de browser niets en komt er
       ook geen "seeked" terug — dan zou de wachtrij voorgoed blijven hangen. */
    if (Math.abs(t - video.currentTime) < 0.002) { seeking = false; return; }
    seeking = true;
    video.currentTime = t;
  }

  function seekTo(t) {
    wantTime = t;
    if (!seeking) pump();
  }

  video.addEventListener("seeked", () => {
    seeking = false;
    if (wantTime !== null) pump();
    const deg = video.duration ? Math.round((video.currentTime / video.duration) * 360) % 360 : 0;
    stage.setAttribute("aria-valuenow", deg);
    stage.setAttribute("aria-valuetext", deg + " graden");
  });

  /* Eén rondje voordoen zodat je ziet dát hij draait. We spelen de video niet af
     maar scrubben hem: afspelen kan een browser weigeren (autoplay-beleid), seeken
     nooit. Zo loopt alles — intro, slepen, toetsenbord — over hetzelfde pad. */
  function intro() {
    if (calm || introDone || !video.duration) return;
    introDone = true;
    const span = video.duration * 0.999;   // net niet het einde, anders klapt hij terug naar 0
    const start = performance.now();
    const duur = spin.hasAttribute("data-spin-auto") ? 2200 : 1400;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duur);
      seekTo(span * (1 - Math.pow(1 - p, 2)));   // licht uitlopend; een draaiend object hoort niet te slepen
      if (p < 1 && !dragging) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* Wacht met het rondje tot de galerij echt in beeld staat. Anders draait hij
     onzichtbaar rond bij iemand die met een ankerlink halverwege de pagina binnenkomt. */
  function introWhenSeen() {
    if (!("IntersectionObserver" in window)) { intro(); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        if (video.readyState >= 2) intro();
        else video.addEventListener("loadeddata", intro, { once: true });
      });
    }, { threshold: 0.5 });
    io.observe(spin);
  }

  function open(color) {
    const src = sources[color];
    if (!src) return;
    const fresh = loadedFor !== color;
    if (fresh) {
      video.preload = "auto";   // in de HTML staat "none": pas laden als hij echt open gaat
      video.src = src;
      video.load();
      loadedFor = color;
      introDone = false;
      spin.classList.remove("is-used");
    }
    mainMedia.hidden = true;
    spin.hidden = false;
    document.querySelectorAll("[data-thumb]").forEach((t) => t.classList.remove("is-active"));
    thumb.classList.add("is-active");
    if (!fresh) intro();
    else if (spin.hasAttribute("data-spin-auto")) introWhenSeen();
    else video.addEventListener("loadeddata", intro, { once: true });
  }

  function close() {
    spin.hidden = true;
    mainMedia.hidden = false;
    thumb.classList.remove("is-active");
  }

  thumb.addEventListener("click", () => open(thumb.dataset.color));

  document.addEventListener("click", (e) => {
    if (e.target.closest("[data-thumb]")) close();
  });

  /* slepen */
  let startX = 0, startT = 0;

  stage.addEventListener("pointerdown", (e) => {
    dragging = true;
    startX = e.clientX;
    startT = video.currentTime;
    spin.classList.add("is-dragging", "is-used");
    stage.setPointerCapture(e.pointerId);
  });

  stage.addEventListener("pointermove", (e) => {
    if (!dragging || !video.duration) return;
    const dx = (e.clientX - startX) / stage.clientWidth;
    seekTo(startT + dx * video.duration);
  });

  const stopDrag = (e) => {
    if (!dragging) return;
    dragging = false;
    spin.classList.remove("is-dragging");
    if (stage.hasPointerCapture(e.pointerId)) stage.releasePointerCapture(e.pointerId);
  };
  stage.addEventListener("pointerup", stopDrag);
  stage.addEventListener("pointercancel", stopDrag);

  /* toetsenbord: pijltjes draaien met een vijftiende slag tegelijk */
  stage.addEventListener("keydown", (e) => {
    if (!video.duration) return;
    const step = video.duration / 24;
    if (e.key === "ArrowRight") { spin.classList.add("is-used"); seekTo(video.currentTime + step); }
    else if (e.key === "ArrowLeft") { spin.classList.add("is-used"); seekTo(video.currentTime - step); }
    else if (e.key === "Home") { seekTo(0); }
    else return;
    e.preventDefault();
  });

  /* Staat de viewer al open bij het laden (draaibeeld als hoofdbeeld)? Dan hier de bron
     zetten. Niet doen bij databesparing of 2G: dat is 4-7 MB die niemand gevraagd heeft.
     In dat geval valt hij terug op de foto en blijft de 360-knop gewoon staan. */
  if (spin.hasAttribute("data-spin-auto") && !window.matchMedia("(max-width: 749px)").matches) {
    const verbinding = navigator.connection || {};
    const zuinig = verbinding.saveData === true || /(^|-)2g$/.test(verbinding.effectiveType || "");
    if (zuinig) close();
    else open(thumb.dataset.color);
  }

  /* andere kleur gekozen: knop tonen of verbergen, viewer sluiten */
  document.addEventListener("formvolt:color", (e) => {
    const color = e.detail.color;
    const has = Boolean(sources[color]);
    thumb.hidden = !has;
    thumb.dataset.color = color || "";
    if (!spin.hidden) has ? open(color) : close();
  });
}

/* ---------- collectie: filters auto-submit ---------- */
function initFilters() {
  const form = document.querySelector("[data-filter-form]");
  if (!form) return;
  form.addEventListener("change", (e) => {
    if (e.target.matches('input[type="checkbox"], select[data-sort]')) form.submit();
  });
}

/* ---------- start ---------- */
document.addEventListener("DOMContentLoaded", () => {
  initTopbar();
  initHeader();
  initReveals();
  initCart();
  initVariantPicker();
  initSpin();
  initFilters();
});
